import { forEachObj } from 'remeda'
import { getDocument, getSessionStorage } from '@/helpers/globals'
import type { NonNullableComputedObject } from '@/types/utils'
import { type ReactiveCookies, useCookies } from '@/plugins/cookie-plugin'
import { useRoute } from 'vue-router'

const utmParamNames = [
    'client_id',
    'session_id',
    'fbp',
    'gclid',
    'fbclid',
    'ft_utm_source',
    'ft_utm_medium',
    'ft_utm_campaign',
    'lt_utm_source',
    'lt_utm_medium',
    'lt_utm_campaign',
] as const

export type UtmParamNames = (typeof utmParamNames)[number]
export type UtmParams = Record<UtmParamNames, string>

export function useUtmParams(): ReactiveCookies<UtmParams> {
    // ! IMPORTANT: initialize all utm params with empty string
    // The API will not accept null values
    const utmParams = useCookies(
        utmParamNames.reduce(
            (acc, paramName) => ({
                ...acc,
                [paramName]: '',
            }),
            {} as UtmParams
        )
    )

    const query = useRoute().query
    const sessionStorage = getSessionStorage()

    // Store query params from UTM url
    forEachObj(utmParams, (param, paramName) => {
        const urlParam =
            query[paramName.replace('ft_', '').replace('lt_', '')]?.toString()

        if (urlParam) {
            if (paramName.startsWith('ft_') && !param) {
                utmParams[paramName].value = urlParam

                return
            }

            if (paramName.startsWith('lt_')) {
                utmParams[paramName].value = urlParam
                sessionStorage?.setItem('lt_set_from_url', 'true')

                return
            }

            utmParams[paramName].value = urlParam
        }
    })

    // Store query params from non-UTM url
    const document = getDocument()

    let medium: string
    let source: string
    let referrer = document?.referrer || ''
    const baidu_regex = new RegExp('https://www.baidu.com/.*')

    if (baidu_regex.test(referrer)) {
        referrer = 'baidu'
    }

    switch (referrer) {
        case '':
            source = 'direct'
            medium = 'none'
            break

        case 'https://www.google.com/':
            source = 'google'
            medium = 'organic'
            break

        case 'https://www.bing.com/':
            source = 'bing'
            medium = 'organic'
            break

        case 'https://search.yahoo.com/':
            source = 'yahoo'
            medium = 'organic'
            break

        case 'https://yandex.com/':
            source = 'yandex'
            medium = 'organic'
            break

        case 'https://duckduckgo.com/':
            source = 'duckduckgo'
            medium = 'organic'
            break

        case 'baidu':
            source = 'baidu'
            medium = 'organic'
            break

        default:
            medium = 'referral'
            source = referrer
            break
    }

    utmParams.ft_utm_medium.value ||= medium
    utmParams.ft_utm_source.value ||= source

    if (sessionStorage?.getItem('lt_set_from_url') === null) {
        utmParams.lt_utm_medium.value ||= medium
        utmParams.lt_utm_source.value ||= source
    }

    return utmParams as NonNullableComputedObject<typeof utmParams>
}
